import React, { useEffect, useState, createContext, useContext, Component } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Types
export type UserRole = 'super_admin' | 'regional_admin' | 'officer' | 'viewer' | 'public';
export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  region?: string;
  avatar?: string;
}
export interface Permission {
  canViewDashboard: boolean;
  canManageUsers: boolean;
  canManageContent: boolean;
  canManageEvents: boolean;
  canManageCommunities: boolean;
  canManageAssets: boolean;
  canViewAuditLog: boolean;
  canApproveReports: boolean;
  canCreateReports: boolean;
  canViewReports: boolean;
  canEditOwnRegion: boolean;
}
// Permission Matrix
export const ROLE_PERMISSIONS: Record<UserRole, Permission> = {
  super_admin: {
    canViewDashboard: true,
    canManageUsers: true,
    canManageContent: true,
    canManageEvents: true,
    canManageCommunities: true,
    canManageAssets: true,
    canViewAuditLog: true,
    canApproveReports: true,
    canCreateReports: true,
    canViewReports: true,
    canEditOwnRegion: true
  },
  regional_admin: {
    canViewDashboard: true,
    canManageUsers: false,
    canManageContent: true,
    canManageEvents: true,
    canManageCommunities: true,
    canManageAssets: false,
    canViewAuditLog: false,
    canApproveReports: true,
    canCreateReports: true,
    canViewReports: true,
    canEditOwnRegion: true
  },
  officer: {
    canViewDashboard: true,
    canManageUsers: false,
    canManageContent: false,
    canManageEvents: false,
    canManageCommunities: false,
    canManageAssets: false,
    canViewAuditLog: false,
    canApproveReports: false,
    canCreateReports: true,
    canViewReports: true,
    canEditOwnRegion: false
  },
  viewer: {
    canViewDashboard: true,
    canManageUsers: false,
    canManageContent: false,
    canManageEvents: false,
    canManageCommunities: false,
    canManageAssets: false,
    canViewAuditLog: false,
    canApproveReports: false,
    canCreateReports: false,
    canViewReports: true,
    canEditOwnRegion: false
  },
  public: {
    canViewDashboard: false,
    canManageUsers: false,
    canManageContent: false,
    canManageEvents: false,
    canManageCommunities: false,
    canManageAssets: false,
    canViewAuditLog: false,
    canApproveReports: false,
    canCreateReports: false,
    canViewReports: false,
    canEditOwnRegion: false
  }
};
// Role Labels
export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  regional_admin: 'Regional Admin',
  officer: 'Petugas Lapangan',
  viewer: 'Viewer',
  public: 'Publik'
};
// Auth Context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: Permission;
  login: (email: string, password: string, role: 'officer' | 'admin') => Promise<boolean>;
  logout: () => void;
  hasPermission: (permission: keyof Permission) => boolean;
}
const defaultPermissions = ROLE_PERMISSIONS.public;
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  permissions: defaultPermissions,
  login: async () => false,
  logout: () => {},
  hasPermission: () => false
});
// Mock users for demo
const MOCK_USERS: Record<string, User & {
  password: string;
}> = {
  'admin@demo.id': {
    id: '1',
    name: 'Admin Pusat',
    email: 'admin@demo.id',
    password: 'admin123',
    role: 'super_admin',
    region: 'Pusat'
  },
  'regional@demo.id': {
    id: '2',
    name: 'Admin DKI Jakarta',
    email: 'regional@demo.id',
    password: 'regional123',
    role: 'regional_admin',
    region: 'DKI Jakarta'
  },
  'petugas@demo.id': {
    id: '3',
    name: 'Budi Santoso',
    email: 'petugas@demo.id',
    password: 'petugas123',
    role: 'officer',
    region: 'Jakarta Selatan'
  },
  'viewer@demo.id': {
    id: '4',
    name: 'Viewer User',
    email: 'viewer@demo.id',
    password: 'viewer123',
    role: 'viewer',
    region: 'Pusat'
  }
};
// Auth Provider
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Check for existing session
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('auth_user');
      }
    }
    setIsLoading(false);
  }, []);
  const login = async (email: string, password: string, role: 'officer' | 'admin'): Promise<boolean> => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    const mockUser = MOCK_USERS[email];
    if (mockUser && mockUser.password === password) {
      // Validate role matches
      const isAdminRole = ['super_admin', 'regional_admin', 'viewer'].includes(mockUser.role);
      const isOfficerRole = mockUser.role === 'officer';
      if (role === 'admin' && isAdminRole || role === 'officer' && isOfficerRole) {
        const {
          password: _,
          ...userWithoutPassword
        } = mockUser;
        setUser(userWithoutPassword);
        localStorage.setItem('auth_user', JSON.stringify(userWithoutPassword));
        return true;
      }
    }
    return false;
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_user');
  };
  const permissions = user ? ROLE_PERMISSIONS[user.role] : defaultPermissions;
  const hasPermission = (permission: keyof Permission): boolean => {
    return permissions[permission] ?? false;
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    isLoading,
    permissions,
    login,
    logout,
    hasPermission
  }} data-id="element-449">
      {children}
    </AuthContext.Provider>;
}
// Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
// Protected Route Component
interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole[];
  requiredPermission?: keyof Permission;
  redirectTo?: string;
}
export function ProtectedRoute({
  children,
  requiredRole,
  requiredPermission,
  redirectTo = '/login'
}: ProtectedRouteProps) {
  const {
    user,
    isAuthenticated,
    isLoading,
    hasPermission
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate(redirectTo, {
        state: {
          from: location.pathname
        }
      });
      return;
    }
    if (requiredRole && user && !requiredRole.includes(user.role)) {
      navigate('/unauthorized');
      return;
    }
    if (requiredPermission && !hasPermission(requiredPermission)) {
      navigate('/unauthorized');
      return;
    }
  }, [isAuthenticated, isLoading, user, requiredRole, requiredPermission, navigate, location, hasPermission, redirectTo]);
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-id="element-450">
        <div className="text-center" data-id="element-451">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" data-id="element-452"></div>
          <p className="text-gray-600" data-id="element-453">Memuat...</p>
        </div>
      </div>;
  }
  if (!isAuthenticated) {
    return null;
  }
  if (requiredRole && user && !requiredRole.includes(user.role)) {
    return null;
  }
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return null;
  }
  return <>{children}</>;
}
// Permission Gate Component
interface PermissionGateProps {
  children: ReactNode;
  permission: keyof Permission;
  fallback?: ReactNode;
}
export function PermissionGate({
  children,
  permission,
  fallback = null
}: PermissionGateProps) {
  const {
    hasPermission
  } = useAuth();
  if (!hasPermission(permission)) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
}